/**
 * registerAccessTools — client-side registration for the agent access /
 * sharing tool surface (Design note I, the `client-executed` flavor).
 *
 * Side-effect module: imported once from agentTools.ts (which hooks.ts +
 * agui-to-stream.ts already side-effect-import) so the registry is
 * populated before the AG-UI stream is consumed. Registering here ONLY
 * sets render/handler/transport — it NEVER advertises a tool to the model
 * (the worker tool files stay the sole authority; see registerAgentTool.ts
 * HARD RULE).
 *
 * TWO halves of the access surface:
 *   • READ actions (share_link.get_default/list,
 *     manage_access.list/app_grants) run as `client-executed` frontend tools
 *     registered below — the browser performs the REST call with the user's
 *     session and re-POSTs the result over the AG-UI tool-result channel. No
 *     approval UI. (The `team` tool's reads moved SERVER-SIDE — issue #386 —
 *     so it is no longer registered here.)
 *   • MUTATION actions arrive as `accessRequest` question metadata and are
 *     gated behind AccessApprovalCard (MessageList) — NOT registered here;
 *     `parseAccessRequest` (exported below) is the detector.
 *
 * The registered NAME is what the worker advertises the read tool as. The
 * worker contract is unsettled on this, so we register the same
 * name-agnostic dispatch handler under every plausible name (the 4 tool
 * ids + a couple of umbrella names). Whichever name the worker uses, it
 * resolves; the handler dispatches purely on `args.tool` + `args.action`
 * (falling back to the bound name when `args.tool` is absent — the case
 * where the worker advertises 4 separate tools). Reconcile the final
 * name(s) with the worker at integration.
 */
import {
  registerAgentTool,
  type AgentToolEntry,
  type AgentToolResult,
} from './registerAgentTool.js'
import {
  performAccessAction,
  getAccessActionSpec,
  type AccessRequestMeta,
} from './accessToolActions.js'
import type { QuestionRequest } from './hooks.js'

/**
 * Small typed helper for registering a `client-executed` frontend tool.
 * Reusable by OTHER client handlers (e.g. the preview_screenshot tool in
 * its own file) so they don't hand-assemble the entry shape.
 */
export function registerClientExecutedTool(
  name: string,
  handler: NonNullable<AgentToolEntry['handler']>,
  ready?: AgentToolEntry['ready'],
  argsFrom?: AgentToolEntry['argsFrom'],
): void {
  registerAgentTool(name, { transport: 'client-executed', handler, ready, argsFrom })
}

/** Build a name-agnostic read handler bound to a default tool id (used
 *  when the worker advertises a per-tool name and omits `args.tool`). */
function makeAccessReadHandler(boundToolId: string) {
  return async function accessReadHandler(
    args: Record<string, unknown>,
  ): Promise<AgentToolResult> {
    const tool = (typeof args.tool === 'string' && args.tool) || boundToolId
    const action = typeof args.action === 'string' ? args.action : ''
    if (!action) {
      return { success: false, error: `Missing action for access tool "${tool}"` }
    }
    // These tools are `argsFrom: 'worker'`, so `args` IS the worker's contract
    // and `payload` carries the ids it resolved. A missing payload means the
    // dispatch came from somewhere that doesn't carry one — historically the
    // model's raw tool-call args, which silently produced an id-less request
    // (`GET /api/folders//permissions` → 404) reported only as "Request failed".
    // Refuse loudly instead: an empty payload can never produce a correct call,
    // and a named failure is what makes the next occurrence diagnosable.
    if (!args.payload || typeof args.payload !== 'object') {
      return {
        success: false,
        error: `Internal: "${tool}.${action}" was dispatched without the worker's resolved payload — the request was not sent. This is a client-tool wiring bug, not a permission problem.`,
      }
    }
    return performAccessAction(tool, action, args.payload as Record<string, unknown>)
  }
}

/** Every name we register the read dispatcher under.
 *
 *  `team` is intentionally absent — its reads run server-side in the worker now
 *  (#386). `chain_folder` is still here, but ONLY for a previous-build worker:
 *  since #518A its create runs in the worker after consent, so a current worker
 *  never dispatches to this handler. Removing it would break that skew
 *  direction, since an unresolved action fails outright rather than degrading;
 *  the reasoning lives beside the entry in accessToolActions.ts. */
export const ACCESS_READ_TOOL_NAMES = [
  'share_link',
  'chain_folder',
  'manage_access',
  // Umbrella fallbacks — harmless if the worker never uses them.
  'access',
  'access_read',
] as const

for (const name of ACCESS_READ_TOOL_NAMES) {
  // `argsFrom: 'worker'` — these handlers need the payload the WORKER resolved
  // (share_link turns a `/papan/`-relative path into a concrete file id, which
  // the browser cannot redo), never the model's raw tool-call args.
  registerClientExecutedTool(name, makeAccessReadHandler(name), undefined, 'worker')
}

/**
 * Detect + parse a MUTATION access-approval question. Mirrors the A2UI /
 * Credentials convention: `questions[0].header === 'AccessRequest'` and a
 * JSON payload in `options[0].description`. Also accepts a bare
 * `{ tool, action, payload, summary }` or a `{ accessRequest: {...} }`
 * wrapper, and falls back to a header-less payload whose `tool.action`
 * exists in the action table (so a header rename doesn't silently break
 * the card). Null ⇒ not ours (falls through to the generic QuestionPrompt).
 */
/**
 * Headers that belong to a DIFFERENT card renderer. A question wearing one of
 * these is that card's, never an access request — see the note in
 * `parseAccessRequest`'s fallback for why claiming them is a forgery surface.
 */
const FOREIGN_CARD_HEADERS = new Set(['A2UI', 'Credentials'])

/**
 * The tools that actually raise an approval card — every `ctx.metadata({
 * accessRequest })` emitter in `features/agent/tools`. A payload claiming
 * anything else is not an access request, whatever header it wears and whoever
 * emitted it.
 */
const ACCESS_CARD_TOOLS = new Set(['manage_access', 'share_link', 'chain_folder', 'team'])

export function parseAccessRequest(
  request: QuestionRequest,
  /**
   * The tool whose call RAISED this question, when the caller knows it.
   *
   * THE ONLY MODEL-PROOF DISCRIMINANT HERE. Everything else on a question is
   * model-writable: `Question.Info.header` is an unconstrained `z.string()` and
   * `QuestionTool` — ungated, active on every turn — passes the model's
   * questions through verbatim, `options[].description` included. So a
   * prompt-injected turn can emit `header: 'AccessRequest'` with a payload
   * naming `share_link.create`, and the browser renders a real approval card
   * whose SENTENCE THE MODEL WROTE; `fileId` and `recipientGrants` are both in
   * HIDDEN_KEYS, so that sentence is all the human sees, and one Approve click
   * mints an outsider link with the victim's own session.
   *
   * `request.tool.callID` is stamped SERVER-SIDE by `Question.createRequest`
   * from the emitting tool call, so the model cannot choose it. Requiring the
   * emitter to BE the tool the payload claims is therefore not spoofable: a
   * forged card raised from the `question` tool names `share_link` and is
   * refused.
   *
   * Fails OPEN when the caller cannot resolve a name (an unknown call id, a
   * card recovered after a reload before the tool-call map repopulates) —
   * closing there would silently stop rendering legitimate cards, and it costs
   * nothing against the live attack, whose own call IS active and therefore
   * resolvable.
   */
  emittingTool?: string,
): AccessRequestMeta | null {
  const q = request.questions?.[0]
  if (!q) return null
  const desc = q.options?.[0]?.description
  if (!desc || !desc.startsWith('{')) return null
  let parsed: any
  try {
    parsed = JSON.parse(desc)
  } catch {
    return null
  }
  const meta = parsed?.accessRequest ?? parsed
  if (!meta || typeof meta.tool !== 'string' || typeof meta.action !== 'string') {
    return null
  }
  // The claimed tool must be one that ACTUALLY raises access cards. Requiring
  // only `emitter === meta.tool` is satisfied by a tool naming ITSELF: the
  // ungated `question` tool can emit `{tool:'question', action:'grant_access',
  // summary:'Grant "Manage & Edit" on Payroll to alice@acme.com', payload:{…}}`,
  // and emitter and claim then match. Nothing is mutated — `getAccessActionSpec`
  // misses, so the card replies consent-only — but the human is shown, and
  // clicks Approve on, a card pixel-identical to a real grant (shield, "Grant
  // access" title, Person/Access rows), and the tool reports that approval back
  // to the model, which can cite it. An allow-list is what actually holds; the
  // pairwise check alone does not.
  if (!ACCESS_CARD_TOOLS.has(meta.tool)) return null
  // …and the emitter must BE that tool — see `emittingTool`.
  // PREFER the server-stamped name on the request: it rides the question
  // itself, so it survives a reload and the `/pending` replay, where the
  // client's own tool-call map is empty and STAYS empty (nothing repopulates
  // it — the AG-UI re-attach discards the transcript snapshot, so the blocked
  // call's `tool-call-start` is never replayed). Without it the check was
  // inert for the whole remainder of any tool call that outlived a reload.
  const emitter = request.tool?.toolName ?? emittingTool
  if (emitter && emitter !== meta.tool) return null
  // Header is the primary discriminator; if it's absent/renamed, only
  // treat this as an access request when the tool.action is one we know
  // (avoids hijacking an unrelated question that happens to carry JSON).
  const headerOk =
    q.header === 'AccessRequest' ||
    q.header === 'access_request' ||
    q.header === 'AccessApproval'
  if (!headerOk) {
    // Defence in depth, and independent of the emitter check above: that one
    // fails open on an unresolvable call id, and this one does not need to
    // resolve anything. A header belonging to ANOTHER card type is never an
    // access request, however well-formed its JSON looks. `render_ui` puts
    // `args.surface` straight into `options[0].description` under header 'A2UI',
    // and `parseAccessRequest` runs BEFORE `parseA2UIQuestion` in MessageList.
    // So a model could emit a surface carrying `tool`/`action`/`summary`/
    // `payload`, and the browser would render an AccessApprovalCard whose
    // SENTENCE THE MODEL WROTE — one Approve click performing a real mutation
    // with the user's session (e.g. share_link.create minting an outsider link
    // with edit to an attacker address; `fileId` and `recipientGrants` are both
    // in HIDDEN_KEYS, so the forged summary is all the human sees).
    //
    // That defeats the rule these tools state everywhere else — a card must say
    // what will actually happen, and never from a model-supplied label.
    // Pre-existing, found by a whole-branch review.
    if (q.header && FOREIGN_CARD_HEADERS.has(q.header)) return null
    // Header absent/renamed: only claim this question if the tool.action
    // is one we actually execute (avoids hijacking an unrelated JSON
    // question). Also require it to be a MUTATION — reads never pend a
    // question, so a read action here would be a miswire.
    const spec = getAccessActionSpec(meta.tool, meta.action)
    return spec && spec.kind === 'mutation' ? toMeta(meta, q.question) : null
  }
  return toMeta(meta, q.question)
}

function toMeta(meta: any, fallbackSummary?: string): AccessRequestMeta {
  return {
    tool: meta.tool,
    action: meta.action,
    payload:
      meta.payload && typeof meta.payload === 'object' ? meta.payload : {},
    summary:
      typeof meta.summary === 'string' && meta.summary
        ? meta.summary
        : fallbackSummary ?? '',
  }
}

